/*
 * SoftClutch – randomized product cards (v2).
 *
 * Loaded (parser-blocking, right after the markup) by:
 *   - sections/product-list.liquid  ([data-sc-shuffle], homepage "Featured Products"):
 *       picks the 8 visible products from the rendered cards + <template data-sc-pool>
 *       (>= N beanies, >= N hats, never sweats, rest mostly hoodies/tees), shuffles them,
 *       then applies a random colour to each card.
 *   - sections/main-collection.liquid ([data-sc-root], every collection page incl. /collections/all):
 *       applies a random colour to each card; a MutationObserver handles cards added by
 *       infinite scroll and cards re-rendered by filtering/sorting.
 *
 * Colour pick (per card, only colours the product sells, and only colours that have an image
 * of the SAME view as the card's featured image – front stays front, back stays back):
 *   palette groups Black / White / Cream (Sand, Natural, Cream, Bone, Ivory, Stone) /
 *   Military Green (Military Green, Olive) share PALETTE_SHARE (82%) equally; all other colours
 *   share the remaining 18%. Accessories (beanies/hats) always use their front design image.
 * The card's hover/second image is set to the same colour's complementary view
 * (front <-> printed back; accessories: worn front). Other slides are hidden, and views with
 * nothing printed (alt "plain back" / "worn back" / "side") are never shown.
 * Every product link in the card gets ?variant=<first available variant of that colour>.
 *
 * Data comes from <script type="application/json" data-sc-data> rendered by
 * snippets/sc-card-data.liquid (views are read from image alt text "<Title>, <view>, <Colour>").
 * Anything missing or failing leaves the server-rendered card untouched.
 */
(function () {
  'use strict';
  if (window.SCShuffle) {
    window.SCShuffle.scan();
    return;
  }

  var CFG = {
    palette: {
      black: ['Black'],
      white: ['White'],
      cream: ['Sand', 'Natural', 'Cream', 'Bone', 'Ivory', 'Stone'],
      green: ['Military Green', 'Olive']
    },
    paletteShare: 0.82,
    extraAccessoryChance: 0.12, // per free homepage slot, after the minimum beanies/hats
    blockedViews: ['plain back', 'worn back', 'side'],
    // Page-level balancing: a palette group already shown n times on this page gets
    // weight 1/(1+n)^balance, so the four palette colours even out across the grid
    // (most products sell Black; few sell Cream/Military Green). 0 = off.
    balance: 1
  };
  var shownGroups = { black: 0, white: 0, cream: 0, green: 0 };

  var paletteOf = {};
  Object.keys(CFG.palette).forEach(function (g) {
    CFG.palette[g].forEach(function (c) {
      paletteOf[c.toLowerCase()] = g;
    });
  });

  var rng = function () {
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        var a = new Uint32Array(1);
        window.crypto.getRandomValues(a);
        return a[0] / 4294967296;
      }
    } catch (e) {}
    return Math.random();
  };

  function randInt(n) {
    return Math.floor(rng() * n);
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = randInt(i + 1);
      var t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  function readData(item) {
    if (item.__scData !== undefined) return item.__scData;
    var d = null;
    try {
      var el = item.querySelector('script[data-sc-data]');
      if (el) d = JSON.parse(el.textContent);
    } catch (e) {
      d = null;
    }
    item.__scData = d;
    return d;
  }

  /* ---------- colour choice ---------- */

  // Returns { color, id, primary, secondary } or null (keep the default card).
  function pickColor(d) {
    if (!d || !d.pv || !d.v || !d.m) return null;
    var byColor = {};
    for (var i = 0; i < d.m.length; i++) {
      var e = d.m[i];
      if (!byColor[e[0]]) byColor[e[0]] = {};
      if (!byColor[e[0]][e[1]]) byColor[e[0]][e[1]] = { f: e[2], w: e[3], h: e[4] };
    }
    var cands = Object.keys(d.v).filter(function (c) {
      return byColor[c] && byColor[c][d.pv];
    });
    if (!cands.length) return null;

    var groups = {};
    var others = [];
    cands.forEach(function (c) {
      var g = paletteOf[c.toLowerCase()];
      if (g) (groups[g] = groups[g] || []).push(c);
      else others.push(c);
    });
    var gk = Object.keys(groups);
    var weights = [];
    var gw = {};
    var gwSum = 0;
    gk.forEach(function (g) {
      gw[g] = Math.pow(1 + (shownGroups[g] || 0), -(CFG.balance || 0));
      gwSum += gw[g];
    });
    if (gk.length) {
      var palShare = others.length ? CFG.paletteShare : 1;
      gk.forEach(function (g) {
        groups[g].forEach(function (c) {
          weights.push([c, (palShare * gw[g]) / gwSum / groups[g].length]);
        });
      });
      if (others.length) {
        others.forEach(function (c) {
          weights.push([c, (1 - CFG.paletteShare) / others.length]);
        });
      }
    } else {
      others.forEach(function (c) {
        weights.push([c, 1 / others.length]);
      });
    }
    var r = rng();
    var color = weights[weights.length - 1][0];
    for (var k = 0; k < weights.length; k++) {
      r -= weights[k][1];
      if (r < 0) {
        color = weights[k][0];
        break;
      }
    }
    var pg = paletteOf[color.toLowerCase()];
    if (pg) shownGroups[pg] = (shownGroups[pg] || 0) + 1;
    return {
      color: color,
      id: d.v[color],
      primary: byColor[color][d.pv],
      secondary: d.sv ? byColor[color][d.sv] || null : null,
      base: d.b || ''
    };
  }

  /* ---------- DOM helpers ---------- */

  function absUrl(u) {
    return new URL(u, window.location.href);
  }

  function imgUrl(base, file) {
    if (/^(https?:)?\/\//.test(file) || file.charAt(0) === '/') return file;
    return base + file;
  }

  function samePath(a, b) {
    try {
      return absUrl(a).pathname === absUrl(b).pathname;
    } catch (e) {
      return false;
    }
  }

  function buildSrcset(oldSrcset, src, maxW) {
    var widths = [];
    (oldSrcset || '').split(',').forEach(function (part) {
      var m = part.trim().match(/\s(\d+)w$/);
      if (m) widths.push(parseInt(m[1], 10));
    });
    if (!widths.length) widths = [240, 352, 640, 832, 1200, 1600, 1920];
    var out = [];
    widths.forEach(function (w) {
      if (maxW && w > maxW) return;
      var u = absUrl(src);
      u.searchParams.set('width', String(w));
      out.push(u.href + ' ' + w + 'w');
    });
    if (!out.length) {
      var u2 = absUrl(src);
      u2.searchParams.set('width', String(maxW || 1200));
      out.push(u2.href + ' ' + (maxW || 1200) + 'w');
    }
    return out.join(', ');
  }

  function setImage(img, base, im, alt) {
    if (!img || !im) return;
    var src = imgUrl(base, im.f);
    if (samePath(img.getAttribute('src') || '', src)) return;
    var cur = absUrl(img.getAttribute('src') || src);
    var curW = parseInt(cur.searchParams.get('width') || '', 10) || 1200;
    if (im.w && curW > im.w) curW = im.w;
    var s = absUrl(src);
    s.searchParams.set('width', String(curW));
    img.setAttribute('srcset', buildSrcset(img.getAttribute('srcset'), src, im.w));
    img.setAttribute('src', s.href);
    if (im.w && im.h) {
      img.setAttribute('width', String(im.w));
      img.setAttribute('height', String(im.h));
    }
    if (alt) img.setAttribute('alt', alt);
    if (img.hasAttribute('data-max-resolution')) {
      var hi = absUrl(src);
      hi.searchParams.set('width', String(Math.min(3840, im.w || 3840)));
      img.setAttribute('data-max-resolution', hi.href);
    }
  }

  function viewOf(img) {
    var alt = (img && img.getAttribute('alt')) || '';
    var p = alt.split(', ');
    return p.length > 2 ? p[p.length - 2] : '';
  }

  function slideImg(slide) {
    return slide.querySelector('img.product-media__image') || slide.querySelector('img');
  }

  // Shows only [primary, secondary?] and never a blank view.
  function normalizeSlides(item, pick, title) {
    var gallery = item.querySelector('.card-gallery');
    if (!gallery) return;
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('slideshow-slide'));
    var primaryImg;
    if (!slides.length) {
      primaryImg = gallery.querySelector('img.product-media__image');
      if (pick) setImage(primaryImg, pick.base, pick.primary, title + ', ' + pickView(item) + ', ' + pick.color);
      return;
    }
    var visible = slides.filter(function (s) {
      return !s.hasAttribute('hidden');
    });
    var primary =
      visible.filter(function (s) {
        return s.getAttribute('aria-hidden') === 'false';
      })[0] || visible[0];
    if (!primary) return;
    var others = visible.filter(function (s) {
      return s !== primary;
    });
    if (pick) {
      setImage(slideImg(primary), pick.base, pick.primary, title + ', ' + pickView(item) + ', ' + pick.color);
    }
    var keep = null;
    if (pick && pick.secondary && others.length) {
      keep = others[0];
      setImage(slideImg(keep), pick.base, pick.secondary, title + ', ' + (readData(item) || {}).sv + ', ' + pick.color);
    } else if (!pick) {
      // No colour data: keep the first non-blank extra slide as the theme intended.
      for (var i = 0; i < others.length; i++) {
        if (CFG.blockedViews.indexOf(viewOf(slideImg(others[i]))) === -1) {
          keep = others[i];
          break;
        }
      }
    }
    others.forEach(function (s) {
      if (s !== keep) s.setAttribute('hidden', '');
    });
    if (!keep) {
      var arrows = gallery.querySelector('slideshow-arrows');
      if (arrows) arrows.style.display = 'none';
      gallery.removeAttribute('on:pointerenter');
      gallery.removeAttribute('on:pointerleave');
    }
  }

  function pickView(item) {
    var d = readData(item);
    return (d && d.pv) || '';
  }

  function applyCard(item, forcedPick) {
    if (item.hasAttribute('data-sc-done')) return;
    item.setAttribute('data-sc-done', '');
    var d = readData(item);
    var pick = forcedPick !== undefined ? forcedPick : pickColor(d);
    var titleEl = item.querySelector('a.product-card__link');
    var title = (titleEl && titleEl.getAttribute('aria-label')) || '';
    try {
      normalizeSlides(item, pick, title);
    } catch (e) {
      if (window.console) console.warn('[sc-shuffle] slides', e);
    }
    if (!pick || !pick.id) return;
    var links = item.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var href = a.getAttribute('href');
      if (!href || href.indexOf('/products/') === -1) continue;
      try {
        var u = absUrl(href);
        u.searchParams.set('variant', String(pick.id));
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (e) {}
    }
    var card = item.querySelector('product-card');
    if (card && card.hasAttribute('data-featured-media-url')) {
      try {
        var fm = absUrl(imgUrl(pick.base, pick.primary.f));
        fm.searchParams.set('width', '500');
        card.setAttribute('data-featured-media-url', fm.href);
      } catch (e) {}
    }
    item.setAttribute('data-sc-color', pick.color);
  }

  /* ---------- homepage: choose + shuffle ---------- */

  function catOf(el) {
    var c = el.getAttribute('data-sc-cat');
    if (c) return c;
    var d = readData(el);
    return (d && d.cat) || 'apparel';
  }

  // ids: [{id, cat}] ; returns ordered array of ids (length <= slots)
  function compose(cands, slots, minB, minH) {
    var b = [],
      h = [],
      a = [],
      o = [];
    shuffle(cands.slice()).forEach(function (c) {
      if (c.cat === 'sweats') return;
      if (c.cat === 'beanie') b.push(c.id);
      else if (c.cat === 'hat') h.push(c.id);
      else if (c.cat === 'apparel') a.push(c.id);
      else o.push(c.id);
    });
    var picks = [];
    picks = picks.concat(b.splice(0, Math.min(minB, slots)));
    picks = picks.concat(h.splice(0, Math.max(0, Math.min(minH, slots - picks.length))));
    var acc = shuffle(b.concat(h));
    while (picks.length < slots) {
      if (acc.length && (rng() < CFG.extraAccessoryChance || (!a.length && !o.length))) picks.push(acc.shift());
      else if (a.length) picks.push(a.shift());
      else if (o.length) picks.push(o.shift());
      else if (acc.length) picks.push(acc.shift());
      else break;
    }
    return shuffle(picks);
  }

  function runShuffle(section) {
    if (section.hasAttribute('data-sc-done')) return;
    section.setAttribute('data-sc-done', '');
    var items = Array.prototype.slice.call(section.querySelectorAll('.resource-list__item[data-sc-pid]'));
    if (!items.length) return;

    var groups = [];
    items.forEach(function (el) {
      var list = el.closest('.resource-list') || section;
      var g = null;
      for (var i = 0; i < groups.length; i++) if (groups[i].list === list) g = groups[i];
      if (!g) groups.push((g = { list: list, items: [] }));
      g.items.push(el);
    });

    var tpl = section.querySelector('template[data-sc-pool]');
    var poolById = {};
    var cands = [];
    var seen = {};
    groups[0].items.forEach(function (el) {
      var id = el.getAttribute('data-sc-pid');
      if (!seen[id]) {
        seen[id] = 1;
        cands.push({ id: id, cat: catOf(el) });
      }
    });
    if (tpl && tpl.content) {
      Array.prototype.forEach.call(tpl.content.querySelectorAll('.resource-list__item[data-sc-pid]'), function (el) {
        var id = el.getAttribute('data-sc-pid');
        if (!seen[id]) {
          seen[id] = 1;
          poolById[id] = el;
          cands.push({ id: id, cat: catOf(el) });
        }
      });
    }
    var minB = parseInt(section.getAttribute('data-sc-min-beanies') || '0', 10) || 0;
    var minH = parseInt(section.getAttribute('data-sc-min-hats') || '0', 10) || 0;
    var order = compose(cands, groups[0].items.length, minB, minH);
    if (order.length < groups[0].items.length) {
      // not enough candidates: keep the server order, still apply colours
      items.forEach(function (el) {
        applyCard(el);
      });
      return;
    }
    var picks = {};

    groups.forEach(function (g) {
      var live = {};
      var markers = g.items.map(function (el) {
        live[el.getAttribute('data-sc-pid')] = el;
        var m = document.createComment('sc-slot');
        el.parentNode.insertBefore(m, el);
        return m;
      });
      g.items.forEach(function (el) {
        el.parentNode.removeChild(el);
      });
      markers.forEach(function (m, i) {
        var id = order[i];
        var el = live[id] || (poolById[id] && document.importNode(poolById[id], true)) || g.items[i];
        m.parentNode.insertBefore(el, m);
        m.parentNode.removeChild(m);
        if (!(id in picks)) picks[id] = pickColor(readData(el));
        try {
          applyCard(el, picks[id]);
        } catch (e) {
          if (window.console) console.warn('[sc-shuffle] card', e);
        }
      });
    });
  }

  /* ---------- collection pages ---------- */

  var observed = [];
  function processRoot(root) {
    var items = root.querySelectorAll('[data-sc-item]:not([data-sc-done])');
    for (var i = 0; i < items.length; i++) {
      // a re-rendered item may carry stale cached data
      items[i].__scData = undefined;
      try {
        applyCard(items[i]);
      } catch (e) {
        if (window.console) console.warn('[sc-shuffle] card', e);
      }
    }
  }

  function watch(root) {
    if (observed.indexOf(root) !== -1 || !window.MutationObserver) return;
    observed.push(root);
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      // microtask: after the morph/append that triggered us, before the next paint
      Promise.resolve().then(function () {
        queued = false;
        processRoot(root);
      });
    });
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-sc-done'] });
  }

  function scan() {
    var s = document.querySelectorAll('[data-sc-shuffle]:not([data-sc-done])');
    for (var i = 0; i < s.length; i++) {
      try {
        runShuffle(s[i]);
      } catch (e) {
        if (window.console) console.warn('[sc-shuffle]', e);
      }
      s[i].classList.remove('sc-pending');
    }
    var roots = document.querySelectorAll('[data-sc-root]');
    for (var j = 0; j < roots.length; j++) {
      processRoot(roots[j]);
      watch(roots[j]);
    }
  }

  window.SCShuffle = { scan: scan, pickColor: pickColor, compose: compose, cfg: CFG, _rng: function (f) { rng = f; } };
  scan();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
})();
